import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import Text from '../../typography'
import styled from '@emotion/styled';
import { theme } from '../../../themes/default-theme';
import { numberFormatterDecimal } from '../../methods/numberFormatterDecimal';
import calculateRepayment from '../../../util/calculateRepayment';
import toPrice from '../../../util/toPrice';
import { ReactSVG } from 'react-svg';
import CalculatorIcon from '../../../../src/assets/icons/calculator-icon.svg';
import ShareIcon from '../../../../src/assets/icons/model-share.svg';
import MailIcon from '../../../../src/assets/icons/model-mail.svg';
import SocialBackground from '../../../../src/assets/model-social-background.png';
import NewModelSharePopup from '../../ui/NewModelSharePopup';
import NewModelMailPopup from '../../ui/NewModelMailPopup';

const Wrapper = styled.div`
    background: ${theme.palette.charcoal}
`

const Container = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${theme.breakpoints.up('lg')} {
        padding: 24px 50px;
    }
    ${theme.breakpoints.down('md')} {
        padding: 24px;
    }
    ${theme.breakpoints.down('sm')} {
        padding: 16px 20px;
    }
`
const PriceSection = styled.div`

`

const PriceContainer = styled.div`
    display: flex;
    sup {
        color: ${theme.palette.white};
    }
    ${theme.breakpoints.up('md')} {
        gap: 7px;
        h4 {
            font-size: 28px;
        }
        sup {
            font-size: 16px; 
        }
    }
    ${theme.breakpoints.down('sm')} {
        gap: 4px;
        h4 {
            font-size: 15px; 
        }
        sup {
            font-size: 14px; 
        }
    }
`

const PriceText = styled.div`
    > div {
        margin-bottom: 1px;
    }
    ${theme.breakpoints.up('md')} {
        > div {
            font-size: 15px;
        }
    }
    ${theme.breakpoints.down('sm')} {
        float: left;
        margin-top: -2px;
        margin-right: 5px;
        > div {
            font-size: 14px;
        }
    }
`
const FinancePerWeek = styled.div`
    display: flex;
    align-items: center;
    line-height: 23px;
    span {
        font-weight: 800;
    }
    ${theme.breakpoints.up('md')} {
        gap: 14px;
        margin-top: 9px;
        > div {
            font-size: 15px;
        }
    }
    ${theme.breakpoints.down('sm')} {
        gap: 9px;
        margin-top: 3px;
        > div {
            font-size: 11px;
        }
    }
`

const IconCalc = styled.div`
    ${theme.breakpoints.up('md')} {
        &, div, svg {
            display: block;
            width: 17px;
            height: 17px;
        }
    }
    ${theme.breakpoints.down('sm')} {
        &, div, svg {
            display: block;
            width: 12px;
            height: 12px;
        }
    }
`

const SocialSection = styled.div`
    display: flex;
    align-items: center;
    > div {
        cursor: pointer;
        > div {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
        }
    }
    ${theme.breakpoints.up('md')} {
        gap: 18px;
        svg {
            display: block;
        }
        > div {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: url(${SocialBackground}) no-repeat;
            background-size: cover;
            &:first-child {
                svg {
                    width: 17px;
                    height: 18px;
                }
            }
            &:last-child {
                svg {
                    width: 20px;
                    height: 16px;
                }
            }
        }
    }
    ${theme.breakpoints.down('sm')} {
        gap: 12px;
        svg {
            display: block;
        }
        > div {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            background: url(${SocialBackground}) no-repeat;
            background-size: cover;
            &:first-child {
                svg {
                    width: 11px;
                    height: 12px;
                }
            }
            &:last-child {
                svg {
                    width: 14px;
                    height: 10px;
                }
            }
        }
    }
`

const modelPriceDefaultPropRates = {};

const ModelPrice = (props) => {
    props = {
        ...props,
        price: typeof props.price === "undefined" ? null : props.price,
        additionalPrice: typeof props.additionalPrice === "undefined" ? 0 : props.additionalPrice,
        rates: typeof props.rates === "undefined" ? modelPriceDefaultPropRates : props.rates,
        buildUrl: typeof props.buildUrl === "undefined" ? '' : props.buildUrl,
        country: typeof props.country === "undefined" ? null : props.country
    };

    const {
        price,
        additionalPrice,
        rates,
        buildUrl,
        country
    } = props;

    const [totalPrice, setTotalPrice] = useState(price && parseFloat(price) + parseFloat(additionalPrice));
    const [financePerWeek, setFinancePerWeek] = useState(0);
    const [sharePopupOpen, setSharePopupOpen] = useState(false);
    const [mailPopupOpen, setMailPopupOpen] = useState(false);

    useEffect(() => {
        setFinancePerWeek(calculateRepayment({
            rates: rates,
            paymentFrequency: 52,
            price: (price && parseFloat(price)) + (additionalPrice && parseFloat(additionalPrice))
        }).toFixed(0));
    }, [price, rates]);

    const handleSharePopup = (e) => {
        e.preventDefault();
        setSharePopupOpen(true);
    }

    const handleSharePopupStatus = () => {
        setSharePopupOpen(false);
    }

    const handleMailPopup = (e) => {
        e.preventDefault();
        setMailPopupOpen(true);
    }

    const handleMailPopupStatus = () => {
        setMailPopupOpen(false);
    }

    return (
        <>
            {sharePopupOpen &&
                <NewModelSharePopup shareStatus={handleSharePopupStatus} buildUrl={buildUrl} />
            }
            {mailPopupOpen &&
                <NewModelMailPopup mailStatus={handleMailPopupStatus} country={country} res={props.res && props.res} buildUrl={buildUrl} thankyouPageUrl={props.thankyouPageUrl && props.thankyouPageUrl} />
            }
            <Wrapper>
                <Container>
                    {price &&
                        <PriceSection>
                            <PriceText>
                                <Text variant="body1" colour="white">Drive away from</Text>
                            </PriceText>
                            <PriceContainer>
                                <Text variant="h4" colour="white" weight={800}>${numberFormatterDecimal((price && parseFloat(price)) + (additionalPrice && parseFloat(additionalPrice)))}</Text><sup>*</sup>
                            </PriceContainer>
                            {financePerWeek &&
                                <FinancePerWeek>
                                    <IconCalc>
                                        <ReactSVG src={CalculatorIcon} />
                                    </IconCalc>
                                    <Text variant="body1" colour="white">Finance from <span>${toPrice(calculateRepayment({
                                        rates: rates,
                                        paymentFrequency: 52,
                                        price: (price && parseFloat(price)) + (additionalPrice && parseFloat(additionalPrice))
                                    }).toFixed(0))}</span> per week^</Text>
                                </FinancePerWeek>
                            }
                        </PriceSection>
                    }
                    <SocialSection>
                        {buildUrl &&
                            <>
                                <ReactSVG src={ShareIcon} onClick={(e) => { handleSharePopup(e) }} />
                                <ReactSVG src={MailIcon} onClick={(e) => { handleMailPopup(e) }} />
                            </>
                        }
                    </SocialSection>
                </Container>
            </Wrapper>
        </>
    )
}

ModelPrice.propTypes = {
    price: propTypes.any,
    additionalPrice: propTypes.any,
    rates: propTypes.object,
    buildUrl: propTypes.string,
    country: propTypes.string
}

export default ModelPrice